// ────────────────────────────────────────────────────────────────────
// Acrostic Notes — localized playing-card names.
//
// For each supported language we spell the value and suit exactly the
// way a native speaker would name that card OUT LOUD. The two parts
// are concatenated (no separator) in the order native to the language:
//
//   • VS = VALUE + SUIT   (English "seven of hearts"  → SEVENHEARTS)
//   • SV = SUIT  + VALUE  (Dutch    "hartenzeven"      → HARTENZEVEN
//                          German   "Herzsieben"       → HERZSIEBEN)
//
// A magician armed with this file can whisper the card's Dutch/German
// name and the audience will scan first-letters in their own tongue —
// no code-switching mid-trick.
//
// Non-Latin-script languages (ja / ko / zh / ar / he / hi / ru / uk /
// el) intentionally have no entry here — they fall through to `en`,
// same policy as LETTER_BANKS in `./acrosticBanks.js`.
//
// All strings use ASCII first letters where possible (no accents on
// the leading char, no digraphs like "IJ" — the acrostic is a run of
// single Latin letters, one per title).
// ────────────────────────────────────────────────────────────────────

// Canonical card values shared across languages (input keys).
// A card is `{ value: "7" | "J" | ..., suit: "H" | "D" | "C" | "S" }`.

const en = {
  order: "VS",
  values: {
    A: "ACE", "2": "TWO", "3": "THREE", "4": "FOUR", "5": "FIVE",
    "6": "SIX", "7": "SEVEN", "8": "EIGHT", "9": "NINE", "10": "TEN",
    T: "TEN", J: "JACK", Q: "QUEEN", K: "KING",
  },
  suits: { H: "HEARTS", D: "DIAMONDS", C: "CLUBS", S: "SPADES" },
};

const nl = {
  order: "SV",
  values: {
    A: "AAS", "2": "TWEE", "3": "DRIE", "4": "VIER", "5": "VIJF",
    "6": "ZES", "7": "ZEVEN", "8": "ACHT", "9": "NEGEN", "10": "TIEN",
    T: "TIEN", J: "BOER", Q: "VROUW", K: "HEER",
  },
  suits: { H: "HARTEN", D: "RUITEN", C: "KLAVEREN", S: "SCHOPPEN" },
};

const de = {
  order: "SV",
  values: {
    A: "AS", "2": "ZWEI", "3": "DREI", "4": "VIER", "5": "FUENF",
    "6": "SECHS", "7": "SIEBEN", "8": "ACHT", "9": "NEUN", "10": "ZEHN",
    T: "ZEHN", J: "BUBE", Q: "DAME", K: "KOENIG",
  },
  suits: { H: "HERZ", D: "KARO", C: "KREUZ", S: "PIK" },
};

const es = {
  order: "VS",
  values: {
    A: "AS", "2": "DOS", "3": "TRES", "4": "CUATRO", "5": "CINCO",
    "6": "SEIS", "7": "SIETE", "8": "OCHO", "9": "NUEVE", "10": "DIEZ",
    T: "DIEZ", J: "JOTA", Q: "REINA", K: "REY",
  },
  suits: { H: "CORAZONES", D: "DIAMANTES", C: "TREBOLES", S: "PICAS" },
};

const fr = {
  // "sept de cœur" → we drop the "de" liaison so the acrostic reads as
  // a single spelt-out word ("SEPTCOEUR"). The magician's memorised
  // string is compact and unambiguous.
  order: "VS",
  values: {
    A: "AS", "2": "DEUX", "3": "TROIS", "4": "QUATRE", "5": "CINQ",
    "6": "SIX", "7": "SEPT", "8": "HUIT", "9": "NEUF", "10": "DIX",
    T: "DIX", J: "VALET", Q: "DAME", K: "ROI",
  },
  suits: { H: "COEUR", D: "CARREAU", C: "TREFLE", S: "PIQUE" },
};

const it = {
  order: "VS",
  values: {
    A: "ASSO", "2": "DUE", "3": "TRE", "4": "QUATTRO", "5": "CINQUE",
    "6": "SEI", "7": "SETTE", "8": "OTTO", "9": "NOVE", "10": "DIECI",
    T: "DIECI", J: "FANTE", Q: "REGINA", K: "RE",
  },
  suits: { H: "CUORI", D: "QUADRI", C: "FIORI", S: "PICCHE" },
};

const pt = {
  order: "VS",
  values: {
    A: "AS", "2": "DOIS", "3": "TRES", "4": "QUATRO", "5": "CINCO",
    "6": "SEIS", "7": "SETE", "8": "OITO", "9": "NOVE", "10": "DEZ",
    T: "DEZ", J: "VALETE", Q: "DAMA", K: "REI",
  },
  suits: { H: "COPAS", D: "OUROS", C: "PAUS", S: "ESPADAS" },
};

const pl = {
  order: "VS",
  values: {
    A: "AS", "2": "DWOJKA", "3": "TROJKA", "4": "CZWORKA", "5": "PIATKA",
    "6": "SZOSTKA", "7": "SIODEMKA", "8": "OSEMKA", "9": "DZIEWIATKA",
    "10": "DZIESIATKA", T: "DZIESIATKA", J: "WALET", Q: "DAMA", K: "KROL",
  },
  suits: { H: "KIER", D: "KARO", C: "TREFL", S: "PIK" },
};

// Nordic — SUIT then VALUE, all one word ("hjärter sju" reads as
// "hjärtersju" for the acrostic).
const sv = {
  order: "SV",
  values: {
    A: "ESS", "2": "TVA", "3": "TRE", "4": "FYRA", "5": "FEM",
    "6": "SEX", "7": "SJU", "8": "ATTA", "9": "NIO", "10": "TIO",
    T: "TIO", J: "KNEKT", Q: "DAM", K: "KUNG",
  },
  suits: { H: "HJARTER", D: "RUTER", C: "KLOVER", S: "SPADER" },
};

const no = {
  order: "SV",
  values: {
    A: "ESS", "2": "TO", "3": "TRE", "4": "FIRE", "5": "FEM",
    "6": "SEKS", "7": "SYV", "8": "ATTE", "9": "NI", "10": "TI",
    T: "TI", J: "KNEKT", Q: "DAME", K: "KONGE",
  },
  suits: { H: "HJERTER", D: "RUTER", C: "KLOVER", S: "SPAR" },
};

const da = {
  order: "SV",
  values: {
    A: "ES", "2": "TO", "3": "TRE", "4": "FIRE", "5": "FEM",
    "6": "SEKS", "7": "SYV", "8": "OTTE", "9": "NI", "10": "TI",
    T: "TI", J: "KNAEGT", Q: "DAME", K: "KONGE",
  },
  suits: { H: "HJERTER", D: "RUDER", C: "KLOR", S: "SPAR" },
};

const fi = {
  order: "SV",
  values: {
    A: "ASSA", "2": "KAKKONEN", "3": "KOLMONEN", "4": "NELONEN", "5": "VITONEN",
    "6": "KUUTONEN", "7": "SEITSEMAN", "8": "KAHDEKSAN", "9": "YHDEKSAN", "10": "KYMPPI",
    T: "KYMPPI", J: "SOTAMIES", Q: "KUNINGATAR", K: "KUNINGAS",
  },
  suits: { H: "HERTTA", D: "RUUTU", C: "RISTI", S: "PATA" },
};

const cs = {
  order: "SV",
  values: {
    A: "ESO", "2": "DVOJKA", "3": "TROJKA", "4": "CTYRKA", "5": "PETKA",
    "6": "SESTKA", "7": "SEDMICKA", "8": "OSMICKA", "9": "DEVITKA",
    "10": "DESITKA", T: "DESITKA", J: "KLUK", Q: "DAMA", K: "KRAL",
  },
  suits: { H: "SRDCE", D: "KARO", C: "TREFA", S: "PIKA" },
};

const ro = {
  order: "VS",
  values: {
    A: "AS", "2": "DOI", "3": "TREI", "4": "PATRU", "5": "CINCI",
    "6": "SASE", "7": "SAPTE", "8": "OPT", "9": "NOUA", "10": "ZECE",
    T: "ZECE", J: "VALET", Q: "DAMA", K: "REGE",
  },
  suits: { H: "CUPA", D: "CARO", C: "TREFLA", S: "PICA" },
};

const hu = {
  order: "SV",
  values: {
    A: "ASZ", "2": "KETTES", "3": "HARMAS", "4": "NEGYES", "5": "OTOS",
    "6": "HATOS", "7": "HETES", "8": "NYOLCAS", "9": "KILENCES", "10": "TIZES",
    T: "TIZES", J: "BUBI", Q: "DAMA", K: "KIRALY",
  },
  suits: { H: "KOR", D: "KARO", C: "TREFF", S: "PIKK" },
};

const tr = {
  order: "VS",
  values: {
    A: "AS", "2": "IKI", "3": "UC", "4": "DORT", "5": "BES",
    "6": "ALTI", "7": "YEDI", "8": "SEKIZ", "9": "DOKUZ", "10": "ON",
    T: "ON", J: "VALE", Q: "KIZ", K: "PAPAZ",
  },
  suits: { H: "KUPA", D: "KARO", C: "SINEK", S: "MACA" },
};

const id = {
  order: "VS",
  values: {
    A: "AS", "2": "DUA", "3": "TIGA", "4": "EMPAT", "5": "LIMA",
    "6": "ENAM", "7": "TUJUH", "8": "DELAPAN", "9": "SEMBILAN", "10": "SEPULUH",
    T: "SEPULUH", J: "JEK", Q: "RATU", K: "RAJA",
  },
  suits: { H: "HATI", D: "WAJIK", C: "KERITING", S: "SEKOP" },
};

const CARD_NAMES = {
  en, nl, es, fr, de, it, pt, pl, sv, no, da, fi, cs, ro, hu, tr, id,
};

/**
 * Resolve the card-naming locale for a langCode. Exact code, then
 * base language, then English fallback — same policy as pickLetterBank.
 */
export function pickCardLocale(langCode) {
  if (!langCode || langCode === "auto") return en;
  const direct = CARD_NAMES[langCode];
  if (direct) return direct;
  const base = String(langCode).toLowerCase().split("-")[0];
  return CARD_NAMES[base] || en;
}

/**
 * Compute the acrostic target string for `card` in `langCode`.
 * Returns null when the card can't be encoded (missing value or
 * unknown value/suit key).
 */
export function localizedAcrosticString(card, langCode) {
  if (!card || !card.value) return null;
  const locale = pickCardLocale(langCode);
  const v = String(card.value).toUpperCase();
  const s = card.suit ? String(card.suit).toUpperCase() : "";
  const valueWord = locale.values[v];
  if (!valueWord) return null;
  const suitWord = s ? locale.suits[s] : "";
  // Suit is optional — an armed card without a suit (Joker) just
  // produces the value word regardless of order.
  if (!suitWord) return valueWord;
  return locale.order === "SV" ? suitWord + valueWord : valueWord + suitWord;
}

export { CARD_NAMES };
